import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Card — Luminous Atrium container.
 *
 * Variants:
 * - default: cream-on-white card with hairline border + soft shadow
 * - glass: translucent backdrop-blurred card for atmospheric surfaces
 * - elevated: hero/feature card with stronger lift
 * - flat: borderless utility surface (lists, dense data)
 */

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "glass" | "elevated" | "flat";
  interactive?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = "default", interactive = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-2xl text-card-foreground transition-all duration-300",
        variant === "default" &&
          "bg-card border border-border/60 shadow-[0_8px_24px_-12px_rgba(21,34,27,0.06)]",
        variant === "glass" && "atrium-glass border border-border/40 backdrop-blur-md",
        variant === "elevated" &&
          "bg-card border border-border/50 shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)]",
        variant === "flat" && "bg-card",
        interactive &&
          "hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)] cursor-pointer",
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        "font-sans text-lg font-medium leading-tight tracking-[-0.015em] text-foreground",
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground leading-relaxed", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
